import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import Case from 'case';
import {
  Box, Text, Button,
} from 'grommet';
import { useAppTheme } from '@Components/Context';
import { Seo, Spinning, AppButton } from '@Components/Control';
import { withProductAuth } from '@Components/Layout';
import { AuthNavWrapper } from '@Components/Navigation';
import { ProductFormSingleTextInput } from '@Components/Partial/Product/NewSearch';
import { paths } from '@Components/configs';
import { localAsset } from '@Helpers/asset';
import { TableWrapper } from '../../../Partial/Product/SharedComponents';
import { StyledSVG } from '../../../Partial/Product/StyledSharedComponents';

const TableControls = ({
  focusHighlight,
  primaryText,
  selectedSortOption,
  handleSort,
  iconPath,
}) => (
  <Box direction="row" gap="0.5rem">
    <AppButton
      plain
      overrideHover
      sortButtonStyle
      key="sort"
      textSize="1rem"
      color={primaryText}
      label={selectedSortOption.name}
      highlight={focusHighlight}
      onClick={() => handleSort(selectedSortOption.direction === 'desc' ? 'asc' : 'desc')}
      icon={(
        <StyledSVG
          src={localAsset(iconPath)}
          width="1.1rem"
          height="1.1rem"
          title={selectedSortOption.name}
          $fillColor="var(--page-input-icon)"
        />
      )}
    />
  </Box>
);

TableControls.propTypes = {
  focusHighlight: PropTypes.string.isRequired,
  primaryText: PropTypes.string.isRequired,
  iconPath: PropTypes.string.isRequired,
  selectedSortOption: PropTypes.shape({
    name: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  handleSort: PropTypes.func.isRequired,
};

const ReportsTable = ({
  small,
  primaryText,
  buttonHighlight,
  focusHighlight,
  sortOrder,
  handleSort,
  sectionTitle,
  reportsData = null,
  tableBg = null,
}) => {
  const renderReports = () => {
    if (reportsData && reportsData.length >= 1) {
      return (
        <Box direction="column">
          {reportsData.map(({ embedUid, embedTitle }, i) => (
            <Button plain key={embedUid} href={paths.productReport.replace(':id', embedUid)}>
              <Box
                wrap={false}
                fill="horizontal"
                direction={small ? 'column' : 'row'}
                border={(i !== reportsData.length - 1) && {
                  color: 'var(--nav-border)', size: '1px', style: 'solid', side: 'bottom',
                }}
                pad={{ vertical: '1rem' }}
                align={small ? 'start' : 'center'}
                gap={small ? '0.75rem' : '0'}
              >
                <Text size="1.15rem" weight={600} color={primaryText}>{embedTitle}</Text>
              </Box>
            </Button>
          ))}
        </Box>
      );
    }

    return (
      <Box flex justify="center" align="center">
        <Spinning size="large" color={buttonHighlight} />
      </Box>
    );
  };

  return (
    <Box direction="column">
      <TableWrapper
        small={small}
        header={sectionTitle}
        boxPad={{ horizontal: '1.5rem', top: '1.5rem' }}
        bgOverride={tableBg}
        tableControls={(
          <TableControls
            small={small}
            focusHighlight={focusHighlight}
            primaryText="var(--page-input-text)"
            displayInputValue={null}
            selectedSortOption={sortOrder}
            handleSort={(sortDirection) => handleSort(sortDirection)}
            iconPath={`images/sort-input-icon-${sortOrder.direction}.svg`}
          />
        )}
        round="var(--app-radius)"
        style={{ boxShadow: 'var(--elevation)' }}
      >
        {renderReports()}
      </TableWrapper>
    </Box>
  );
};

ReportsTable.propTypes = {
  small: PropTypes.bool.isRequired,
  sectionTitle: PropTypes.string.isRequired,
  primaryText: PropTypes.string.isRequired,
  buttonHighlight: PropTypes.string.isRequired,
  focusHighlight: PropTypes.string.isRequired,
  tableBg: PropTypes.string,
  sortOrder: PropTypes.shape({
    name: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(['asc', 'desc']).isRequired,
  }).isRequired,
  handleSort: PropTypes.func.isRequired,
  reportsData: PropTypes.arrayOf(PropTypes.shape({
    embedUid: PropTypes.string.isRequired,
    embedTitle: PropTypes.string.isRequired,
  })),
};

const ReportsListPage = ({
  small,
  location,
  companyId,
  companyName,
  categoryReports = null,
  customReportsConfig = null,
}) => {
  const [customDisplayReports, setCustomDisplayReports] = React.useState(null);
  const [categoryDisplayReports, setCategoryDisplayReports] = React.useState(null);
  const [allReports, setAllReports] = React.useState([]);
  const [sortOrder, setSortOrder] = React.useState({ name: 'Sort A-Z', direction: 'desc' });
  const [categorySortOrder, setCategorySortOrder] = React.useState({});
  const [searchValue, setSearchValue] = React.useState('');
  const [searchResults, setSearchResults] = React.useState([]);
  const { theme: { page, button }, isDarkMode } = useAppTheme();

  const customReports = customReportsConfig && customReportsConfig.find(({ metadata }) => (
    metadata?.config?.companyId === companyId && metadata?.config?.companyName === companyName));

  React.useEffect(() => {
    if (!customDisplayReports && customReports?.metadata?.config?.customReports
      && customReports.metadata.config.customReports.length >= 1) {
      const sortedReports = customReports.metadata.config.customReports.sort((a, b) => (
        a.embedTitle.toLowerCase().localeCompare(b.embedTitle.toLowerCase())
      ));

      setCustomDisplayReports(sortedReports);
    }
  }, [customReports]);

  React.useEffect(() => {
    if (!categoryDisplayReports && categoryReports) {
      const filteredEntries = Object.entries(categoryReports).filter(([key]) => key !== 'companyVertical' && key !== 'alerts');
      const formattedReports = filteredEntries.map(([key, value]) => {
        const values = Object.entries(value).map(([k, v]) => {
          const name = v.customReportName || Case.title(k);
          return { ...v, embedTitle: name };
        });

        const sortedValues = values.sort((a, b) => (
          a.embedTitle.toLowerCase().localeCompare(b.embedTitle.toLowerCase())
        ));

        return { [key]: sortedValues };
      });

      const reportsOrder = ['infringementsReports', 'takedownsReports', 'complianceReports', 'insightsReports'];
      const sortedReports = formattedReports.sort((a, b) => (
        reportsOrder.indexOf(Object.keys(a)[0]) - reportsOrder.indexOf(Object.keys(b)[0])
      ));

      setCategoryDisplayReports(sortedReports);
      setCategorySortOrder(Object.fromEntries(Object.keys(categoryReports).map(
        (key) => [key, { name: 'Sort A-Z', direction: 'desc' }],
      )));
    }
  }, [categoryReports]);

  React.useEffect(() => {
    const categoryDisplayValues = categoryDisplayReports?.length > 0
      && categoryDisplayReports.map((category) => (Object.values(category)[0])).flat();

    if (customDisplayReports?.length > 0 && categoryDisplayValues) {
      setAllReports([...customDisplayReports, ...categoryDisplayValues]);
    } else if (customDisplayReports?.length > 0) {
      setAllReports([...customDisplayReports]);
    } else if (categoryDisplayValues) {
      setAllReports([...categoryDisplayValues]);
    }
  }, [customDisplayReports, categoryDisplayReports]);

  React.useEffect(() => {
    if (searchValue) {
      const filteredAllReports = allReports.filter((report) => (
        report.embedTitle.toLowerCase().includes(searchValue.toLowerCase())
      ));
      setSearchResults(filteredAllReports);
    } else {
      setSearchResults([]);
    }
  }, [searchValue]);

  const handleSort = (sortDirection) => {
    const prevReports = [...customDisplayReports];

    if (sortDirection === 'desc') {
      prevReports.sort((a, b) => (
        a.embedTitle.toLowerCase().localeCompare(b.embedTitle.toLowerCase())
      ));
      setSortOrder({ name: 'Sort A-Z', direction: 'desc' });
    } else {
      prevReports.sort((a, b) => (
        b.embedTitle.toLowerCase().localeCompare(a.embedTitle.toLowerCase())
      ));
      setSortOrder({ name: 'Sort Z-A', direction: 'asc' });
    }

    setCustomDisplayReports(prevReports);
  };

  const handleCategorySort = (categoryKey, sortDirection) => {
    const prevReports = [...categoryDisplayReports];

    const categoryIndex = prevReports.findIndex((category) => (
      Object.keys(category)[0] === categoryKey
    ));
    const categoryValue = Object.values(prevReports[categoryIndex])[0];

    if (sortDirection === 'desc') {
      categoryValue.sort((a, b) => (
        a.embedTitle.toLowerCase().localeCompare(b.embedTitle.toLowerCase())
      ));
    } else {
      categoryValue.sort((a, b) => (
        b.embedTitle.toLowerCase().localeCompare(a.embedTitle.toLowerCase())
      ));
    }

    prevReports[categoryIndex] = { [categoryKey]: categoryValue };
    setCategoryDisplayReports(prevReports);
    setCategorySortOrder((prev) => ({
      ...prev,
      [categoryKey]: {
        name: sortDirection === 'asc' ? 'Sort Z-A' : 'Sort A-Z',
        direction: sortDirection === 'asc' ? 'asc' : 'desc',
      },
    }));
  };

  const usePageBg = isDarkMode ? page.background : page.altBg;
  const useSectionBg = isDarkMode ? page.altBg : page.background;

  return (
    <AuthNavWrapper
      small={small}
      location={location}
      bannerProps={{
        title: 'All reports',
        subTitle: 'Analytics',
      }}
    >
      <Seo />
      <Box
        flex
        direction="column"
        background={usePageBg}
        pad="2rem"
        height="100%"
        gap="1.5rem"
      >
        {allReports.length > 0 && (
          <Box direction="column" gap="1rem">
            <Box width={small ? '60%' : '35%'}>
              <ProductFormSingleTextInput
                focusHighlight={page.focusHighlight}
                value={searchValue}
                handleFormValues={(val) => setSearchValue(val)}
                placeholder="Search all reports"
                small={small}
                onClear={() => {
                  setSearchValue('');
                  setSearchResults([]);
                }}
                clearActive={searchValue !== ''}
              />
            </Box>
            {searchResults.length > 0 && (
              <Box
                direction="column"
                width={small ? '80%' : '60%'}
                background={useSectionBg}
                round="var(--app-radius)"
                pad="1rem"
                style={{ boxShadow: 'var(--elevation)' }}
              >
                <Box pad={{ vertical: '0.5rem' }}>
                  <Text size="1rem" weight={400} color={page.primaryText}>Results:</Text>
                </Box>
                {searchResults.map(({ embedTitle, embedUid }, i) => (
                  <Button plain key={embedUid} href={paths.productReport.replace(':id', embedUid)}>
                    <Box
                      wrap={false}
                      fill="horizontal"
                      direction={small ? 'column' : 'row'}
                      border={(i !== searchResults.length - 1) && {
                        color: page.tableBorder, size: '1px', style: 'solid', side: 'bottom',
                      }}
                      pad={{ vertical: '1rem', left: '0.5rem' }}
                      align={small ? 'start' : 'center'}
                      gap={small ? '0.75rem' : '0'}
                    >
                      <Box direction="row" gap="0.5rem">
                        <Text size="1rem" weight={500} color={page.altHighlight}>⟶</Text>
                        <Text size="1.15rem" weight={600} color={page.primaryText}>{embedTitle}</Text>
                      </Box>
                    </Box>
                  </Button>
                ))}
              </Box>
            )}
          </Box>
        )}
        {categoryDisplayReports?.length >= 1 && (
          <Box direction="column" gap="1.5rem">
            {categoryDisplayReports.map((category) => {
              const key = Object.keys(category)[0];
              const value = Object.values(category)[0];

              return (
                <ReportsTable
                  key={key}
                  small={small}
                  primaryText={page.primaryText}
                  buttonHighlight={button.primaryBg}
                  focusHighlight={page.focusHighlight}
                  tableBg={useSectionBg}
                  sortOrder={categorySortOrder[key]}
                  handleSort={(sortDirection) => handleCategorySort(key, sortDirection)}
                  sectionTitle={Case.title(key.replace('Reports', ''))}
                  reportsData={value}
                />
              );
            })}
          </Box>
        )}
        {customDisplayReports?.length > 0 && (
          <ReportsTable
            small={small}
            primaryText={page.primaryText}
            buttonHighlight={button.primaryBg}
            focusHighlight={page.focusHighlight}
            tableBg={useSectionBg}
            sortOrder={sortOrder}
            handleSort={handleSort}
            sectionTitle="Custom"
            reportsData={customDisplayReports}
          />
        )}
      </Box>
    </AuthNavWrapper>
  );
};

function mapStateToProps(state) {
  return {
    customReportsConfig: state.appConfig.customReportsConfig,
    companyId: state.auth.companyId,
    companyName: state.auth.companyName,
  };
}

ReportsListPage.propTypes = {
  small: PropTypes.bool.isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    key: PropTypes.string.isRequired,
  }).isRequired,
  customReportsConfig: PropTypes.arrayOf(PropTypes.any),
  companyId: PropTypes.number.isRequired,
  companyName: PropTypes.string.isRequired,
  categoryReports: PropTypes.objectOf(PropTypes.objectOf(PropTypes.shape({
    embedId: PropTypes.string.isRequired,
    embedUid: PropTypes.string.isRequired,
    customReportName: PropTypes.string,
    applyCompanyFilter: PropTypes.bool,
  }))),
};

export default connect(mapStateToProps, null)(withProductAuth(ReportsListPage));
